import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Iter209bb — Tour dashboard guidato con driver.js.
 *
 * Trigger: mostrato UNA volta quando il pro professional atterra
 * sulla dashboard dopo aver cliccato "Esplora prima la dashboard"
 * nel ProOnboardingModal (Iter209aq). Il segnale è un flag
 * `ht_start_dashboard_tour` in localStorage settato dal modal.
 *
 * Backend gate: `user.dashboard_tour_completed === true` disabilita
 * definitivamente il tour anche se il flag localStorage è presente
 * (idempotenza server-side).
 *
 * Al termine (Fine o Salta tour o Chiudi tour) chiama
 * POST /users/me/dashboard-tour-complete e chiama `refreshUser()`.
 */
export default function DashboardTour() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (!user) return;
    if (started.current) return;
    if (user.role !== "professional") return;
    if (user.parent_user_id) return;
    if (user.dashboard_tour_completed) return;
    let shouldStart = false;
    try { shouldStart = localStorage.getItem("ht_start_dashboard_tour") === "1"; } catch (_) {}
    if (!shouldStart) return;
    started.current = true;
    try { localStorage.removeItem("ht_start_dashboard_tour"); } catch (_) {}

    const markCompleted = async () => {
      try {
        await api.post("/users/me/dashboard-tour-complete");
        if (refreshUser) await refreshUser();
      } catch (_) {}
    };

    const openAddProperty = async () => {
      await markCompleted();
      navigate("/properties?new=1");
    };

    const finalStepPopover = {
      title: "Sei pronto",
      description: `
        <div data-testid="dashboard-tour-final" class="space-y-3">
          <p>Inizia aggiungendo il tuo primo immobile.</p>
          <button
            data-testid="dashboard-tour-add-property"
            id="ht-tour-add-property-btn"
            class="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 py-2"
          >Aggiungi immobile</button>
          <button
            data-testid="dashboard-tour-next-link"
            id="ht-tour-next-link-btn"
            class="w-full text-center text-sm text-slate-500 hover:text-slate-700 underline"
          >Avanti →</button>
        </div>
      `,
      showButtons: [],
    };

    const pwaStepPopover = {
      title: "Housetag anche sullo smartphone",
      description: `
        <div data-testid="dashboard-tour-pwa" class="space-y-4">
          <div class="flex justify-center">
            <div class="w-20 h-28 rounded-xl border-2 border-slate-700 bg-gradient-to-b from-slate-100 to-slate-200 flex flex-col items-center justify-between py-2 shadow-md">
              <div class="w-8 h-1 bg-slate-400 rounded-full"></div>
              <div class="text-2xl font-bold text-purple-600">H</div>
              <div class="w-6 h-6 rounded-full border border-slate-400"></div>
            </div>
          </div>
          <p class="text-sm text-slate-700 text-center">
            Housetag è disponibile anche su smartphone! Apri <strong>housetag.it</strong> dal browser del tuo telefono e aggiungi la app alla schermata home per usarla come una vera app mobile.
          </p>
          <div
            data-testid="dashboard-tour-pwa-guide"
            id="ht-tour-pwa-guide"
            class="hidden rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-3"
          >
            <div>
              <div class="font-semibold text-slate-900 mb-1">Android (Chrome)</div>
              <ol class="list-decimal pl-4 space-y-0.5">
                <li>Apri <strong>housetag.it</strong> in Chrome</li>
                <li>Tocca il menu (⋮) in alto a destra</li>
                <li>Seleziona <strong>Aggiungi a schermata Home</strong></li>
                <li>Conferma: trovi l'icona Housetag sul telefono</li>
              </ol>
            </div>
            <div>
              <div class="font-semibold text-slate-900 mb-1">iOS (Safari)</div>
              <ol class="list-decimal pl-4 space-y-0.5">
                <li>Apri <strong>housetag.it</strong> in Safari</li>
                <li>Tocca l'icona Condividi in basso</li>
                <li>Scorri e seleziona <strong>Aggiungi a schermata Home</strong></li>
                <li>Conferma: trovi l'icona Housetag sul telefono</li>
              </ol>
            </div>
          </div>
          <div class="flex gap-2">
            <button
              data-testid="dashboard-tour-pwa-guide-btn"
              id="ht-tour-pwa-guide-btn"
              class="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 py-2 text-sm"
            >Scopri come</button>
            <button
              data-testid="dashboard-tour-pwa-done-btn"
              id="ht-tour-pwa-done-btn"
              class="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium px-4 py-2 text-sm"
            >Fine</button>
          </div>
        </div>
      `,
      showButtons: [],
    };

    const drv = driver({
      showProgress: true,
      allowClose: false,
      overlayOpacity: 0.6,
      stagePadding: 6,
      stageRadius: 10,
      nextBtnText: "Avanti",
      prevBtnText: "Indietro",
      doneBtnText: "Fine",
      showButtons: ["next", "close"],
      // driver.js v1 usa `closeBtnText` come "Salta tour"
      onCloseClick: async () => {
        await markCompleted();
        drv.destroy();
      },
      onDestroyed: () => {
        // Se l'utente arriva all'ultimo step e clicca "Fine"
        void markCompleted();
      },
      steps: [
        {
          element: '[data-testid="nav-dashboard"]',
          popover: {
            title: "Dashboard",
            description: "La tua centrale operativa. Qui trovi pratiche attive, plafond disponibile e gli ultimi eventi.",
          },
        },
        {
          element: '[data-testid="kpi-active"]',
          popover: {
            title: "Pratiche attive",
            description: "Tutte le pratiche in corso per i tuoi immobili. Clicca per vedere i dettagli.",
          },
        },
        {
          element: '[data-testid="kpi-plafond"]',
          popover: {
            title: "Plafond mensile",
            description: "È il budget mensile che Housetag mette a tua disposizione per pagare i servizi del marketplace. Si rinnova ogni mese; il limite attuale lo trovi sotto il valore (es. \"Limite €500\"). È un credito operativo che Housetag anticipa per tuo conto.",
          },
        },
        {
          element: '[data-testid="kpi-credit"]',
          popover: {
            title: "Credito",
            description: "Si accumula ogni volta che un tuo cliente paga un servizio tramite il link di pagamento. Il credito accumulato viene scalato automaticamente dal plafond usato, riducendo quanto dovuto a Housetag. Sotto il valore trovi \"Da usare sulle prossime pratiche\".",
          },
        },
        {
          element: '[data-testid="nav-immobili"]',
          popover: {
            title: "Immobili",
            description: "Aggiungi e gestisci il tuo portafoglio immobiliare.",
          },
        },
        {
          element: '[data-testid="nav-marketplace"]',
          popover: {
            title: "Marketplace",
            description: "Acquista servizi tecnici per i tuoi immobili: visure, accessi agli atti, perizie e altro.",
          },
        },
        {
          element: '[data-testid="nav-clienti"]',
          popover: {
            title: "Clienti",
            description: "Gestisci l'anagrafica dei tuoi clienti.",
          },
        },
        {
          popover: finalStepPopover,
        },
        {
          popover: pwaStepPopover,
        },
      ],
    });

    // Piccolo delay per assicurarsi che kpi e sidebar siano nel DOM
    const t = setTimeout(() => {
      drv.drive();
      // Wire up i bottoni custom degli ultimi step quando arrivano
      const wire = () => {
        const add = document.getElementById("ht-tour-add-property-btn");
        const nextLink = document.getElementById("ht-tour-next-link-btn");
        const pwaGuideBtn = document.getElementById("ht-tour-pwa-guide-btn");
        const pwaGuide = document.getElementById("ht-tour-pwa-guide");
        const pwaDone = document.getElementById("ht-tour-pwa-done-btn");
        if (add) add.onclick = () => { drv.destroy(); openAddProperty(); };
        if (nextLink) nextLink.onclick = () => { drv.moveNext(); };
        if (pwaGuideBtn && pwaGuide) {
          pwaGuideBtn.onclick = () => {
            pwaGuide.classList.toggle("hidden");
            pwaGuideBtn.textContent = pwaGuide.classList.contains("hidden") ? "Scopri come" : "Nascondi guida";
          };
        }
        if (pwaDone) pwaDone.onclick = () => { drv.destroy(); };
      };
      const iv = setInterval(wire, 200);
      setTimeout(() => clearInterval(iv), 15000);
    }, 400);

    return () => clearTimeout(t);
  }, [user, navigate, refreshUser]);

  return null;
}
